import * as Sentry from "@sentry/react";

// Use localStorage for better PWA compatibility
// Cookies may not persist properly in standalone PWA mode
export function setSession(cname: string, cvalue: string, exdays: number = 0) {
  try {
    // Store in localStorage with base64 encoding for consistency
    localStorage.setItem(cname, window.btoa(cvalue));
    
    // Also set expiry if needed (for future cleanup)
    if (exdays > 0) {
      const d = new Date();
      d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
      localStorage.setItem(`${cname}_expiry`, d.getTime().toString());
    }
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      tags: { feature: "session_storage" },
      extra: { operation: "setSession", key: cname },
    });
  }
}

export function deleteSession(cname: string) {
  try {
    localStorage.removeItem(cname);
    localStorage.removeItem(`${cname}_expiry`);
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      tags: { feature: "session_storage" },
      extra: { operation: "deleteSession", key: cname },
    });
  }
}

export function getSession(cname: string) {
  try {
    // Check if expired
    const expiryStr = localStorage.getItem(`${cname}_expiry`);
    if (expiryStr) {
      const expiry = parseInt(expiryStr, 10);
      if (Date.now() > expiry) {
        deleteSession(cname);
        return "";
      }
    }
    
    const value = localStorage.getItem(cname);
    if (value) {
      return window.atob(value);
    }
  } catch (error) {
    Sentry.captureException(error, {
      level: "warning",
      tags: { feature: "session_storage" },
      extra: { operation: "getSession", key: cname },
    });
  }
  return "";
}

/**
 * @deprecated Use waitForAuthInit from firebaseAuth.ts instead.
 * This function is kept for backward compatibility but should not be used.
 * Firebase SDK now handles auth state directly.
 */
export async function waitForSessionTokens(maxWaitMs: number = 3000): Promise<boolean> {
  const startTime = Date.now();
  const checkInterval = 100;
  
  while (Date.now() - startTime < maxWaitMs) {
    const userId = getSession("userId");
    const role = getSession("role");
    
    // Only check for userId and role now (idToken is fetched from Firebase SDK)
    if (userId && role) {
      return true;
    }
    
    await new Promise(resolve => setTimeout(resolve, checkInterval));
  }
  
  return false;
}
